import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateLocationRequest, UpdateLocationRequest } from '@vagusx/clients';
import { getLocationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook to fetch all locations.
 */
export function useLocations() {
  return useQuery({
    queryKey: queryKeys.locations.list(),
    queryFn: () => getLocationClient().list(),
  });
}

/**
 * Hook to fetch a single location by ID.
 */
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.locations.detail(id ?? ''),
    queryFn: () => getLocationClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a location.
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLocationRequest) => getLocationClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}

/**
 * Hook to update a location.
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLocationRequest }) =>
      getLocationClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}

/**
 * Hook to delete a location.
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getLocationClient().deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
    },
  });
}

/**
 * Hook to fetch providers at a location.
 */
export function useLocationProviders(locationId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.locations.providers(locationId ?? ''),
    queryFn: () => getLocationClient().getProviders(locationId!),
    enabled: !!locationId,
  });
}
